import React, { useState, useEffect } from "react";
import MobileCategoryTree from "./MobileCategoryTree";
import styles from "./MobileMenu.module.css";

// Icons
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import departmentsIcon from "../../static/departments.svg";
import celebrationIcon from "../../static/icon-celebration.svg";

interface MobileMenuProps {
  toggleMenu: () => void;
  categories: any[]; // Replace 'any' with the actual type if available
}

const MobileMenu: React.FC<MobileMenuProps> = ({ toggleMenu, categories }) => {
  const [currentScreen, setCurrentScreen] = useState<any>(null); // Adjust type if needed
  const [currentLevel, setCurrentLevel] = useState(1); // Keep track of the level
  const [categoryHistory, setCategoryHistory] = useState<any[]>([]); // Track the category history

  useEffect(() => {
    // Add body-lock class when component mounts
    document.body.classList.add("body-lock");

    // Remove body-lock class when component unmounts
    return () => {
      document.body.classList.remove("body-lock");
    };
  }, []);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Close the menu if the click was outside the menu container
    if (event.target === event.currentTarget) {
      document.body.classList.remove("body-lock");
      toggleMenu();
    }
  };

  const handleCategorySelect = (category: any) => {
    // Add the current category to the history before navigating to the new one
    setCategoryHistory([...categoryHistory, currentScreen]);

    // Navigate to the selected category
    setCurrentScreen(category);
    setCurrentLevel(currentLevel + 1); // Increase the level when going deeper
  };

  const handleBack = () => {
    if (currentLevel === 2) {
      // If we're on level 2, return to the main menu (reset to initial state)
      setCurrentScreen(null);
      setCurrentLevel(1);
      setCategoryHistory([]); // Clear the history when going back to the main menu
    } else {
      // Go back one level and set the previous category from the history
      const previousCategory = categoryHistory[categoryHistory.length - 1]; // Get the last category from history
      setCurrentScreen(previousCategory);
      setCategoryHistory(categoryHistory.slice(0, -1)); // Remove the last entry from history
      setCurrentLevel(currentLevel - 1); // Decrease the level
    }
  };

  // Render the icon based on category name or id
  const renderCategoryIcon = (categoryName: string) => {
    switch (categoryName) {
      case "Categorías":
        return (
          <img
            src={departmentsIcon}
            alt="Departments Icon"
            className={styles.icon}
            width="24"
            height="24"
          />
        );
      case "Celebraciones":
        return (
          <img
            src={celebrationIcon}
            alt="Celebration Icon"
            className={styles.icon}
            width="24"
            height="24"
          />
        );
      default:
        return null; // Or return a default icon if needed
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.menuContainer}>
        {/* Header with close button - always visible */}
        <div className={styles.menuHeader}>
          <h2>
            {currentScreen
              ? currentScreen.name.length > 20
                ? `${currentScreen.name.substring(0, 20)}...`
                : currentScreen.name
              : "Menú"}
          </h2>
          <button
            className={styles.closeButton}
            onClick={toggleMenu}
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        {currentScreen ? (
          <div className={styles.categoryContainer}>
            <MobileCategoryTree
              category={currentScreen}
              onBack={handleBack}
              onSelect={handleCategorySelect}
              currentLevel={currentLevel} // Pass the current level
              closeMenu={toggleMenu} // Pass the toggleMenu function to close the menu when navigating
            />
          </div>
        ) : (
          <div className={styles.menuContent}>
            <div className={styles.links}>
              <div className={styles.optionContainer}>
                <AccountCircleOutlinedIcon className={styles.icon} />
                <a href="/cuenta">Mi Cuenta</a>
              </div>
              <div className={styles.optionContainer}>
                <StickyNote2OutlinedIcon className={styles.icon} />
                <a href="/historial-compras">Ordenes Pasadas</a>
              </div>
              <div className={styles.optionContainer}>
                <FavoriteBorderOutlinedIcon className={styles.icon} />
                <a href="/lista-de-deseos">Lista de Deseos</a>
              </div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.categoriesSection}>
              <h3 className={styles.sectionTitle}>Explora</h3>
              <div className={styles.categoriesGrid}>
                {/* Render categories with dynamic icons */}
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className={styles.categoryCard}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <div className={styles.categoryIconWrapper}>
                      {renderCategoryIcon(category.name)}
                    </div>
                    <span>{category.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.links}>
              <div className={styles.optionContainer}>
                <HelpOutlineOutlinedIcon className={styles.icon} />
                <a href="/ayuda">Ayuda</a>
              </div>
              <div className={styles.optionContainer}>
                <ModeCommentOutlinedIcon className={styles.icon} />
                <a href="/feedback">Dejanos tu Feedback</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
